import React, { CSSProperties } from "react";
import { FooterColumn } from "./types";

interface FooterExportProps {
  clinicName?: string;
  logoUrl?: string;
  secondLogoUrl?: string;
  description?: string;
  columns?: FooterColumn[];
  phone?: string;
  email?: string;
  address?: string;
  copyright?: string;
  columnAlign?: "left" | "right";
  showClinicName?: boolean;
  showContactIcons?: boolean;
  legalLinks?: { label: string; href: string }[];
  backgroundColor?: string;
  textColor?: string;
  clinicNameColor?: string;
  descriptionColor?: string;
  addressColor?: string;
  phoneColor?: string;
  emailColor?: string;
  copyrightColor?: string;
  minHeight?: number;
  socialLinks?: {
    facebook?: string;
    instagram?: string;
    line?: string;
    youtube?: string;
    tiktok?: string;
    x?: string;
    linkedin?: string;
  };
  textFontFamily?: string;
}

export const FooterExport: React.FC<FooterExportProps> = ({
  clinicName = "Premium Clinic",
  logoUrl,
  secondLogoUrl,
  description,
  columns = [],
  phone,
  email,
  address,
  copyright,
  columnAlign = "left",
  showClinicName = true,
  showContactIcons = true,
  legalLinks = [],
  backgroundColor = "#1f2937",
  textColor = "#ffffff",
  clinicNameColor,
  descriptionColor,
  addressColor,
  phoneColor,
  emailColor,
  copyrightColor,
  minHeight = 0,
  socialLinks,
  textFontFamily,
}) => {
  const hasThai = (s: string) => /[\u0E00-\u0E7F]/.test(s);
  const fontStyle = (text: string): CSSProperties =>
    textFontFamily
      ? { fontFamily: `'${textFontFamily}', var(--font-family), sans-serif` }
      : !hasThai(text) ? { fontFamily: "var(--font-family-en, var(--font-family))" } : {};
  const safeColumns = Array.isArray(columns) ? columns : [];
  const currentYear = new Date().getFullYear();

  // Column alignment is a prop set from the settings panel: "right" pushes the
  // link columns to the right edge (brand left / links right, empty middle).
  // Must stay in sync with the editor Footer component.
  const alignRight = columnAlign === "right";

  const footerStyle: CSSProperties = {
    padding: "48px 24px",
    backgroundColor,
    color: textColor,
    minHeight: minHeight > 0 ? `${minHeight}px` : "auto",
  };

  return (
    <footer style={footerStyle} className="footer-grid">
      <div style={{ maxWidth: "1280px", margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: alignRight ? `1fr repeat(${safeColumns.length}, auto)` : `1fr repeat(${safeColumns.length}, 1fr)`,
            gap: "32px",
            marginBottom: "32px",
          }}
        >
          <div>
            {(logoUrl || secondLogoUrl) && (
              <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
                {logoUrl && <img src={logoUrl} alt={clinicName} style={{ height: "40px", width: "auto" }} />}
                {secondLogoUrl && <img src={secondLogoUrl} alt="" style={{ height: "40px", width: "auto" }} />}
              </div>
            )}
            {showClinicName && (
              <h3 style={{ fontSize: "1.25rem", fontWeight: 700, marginBottom: "16px", marginTop: 0, ...(clinicNameColor ? { color: clinicNameColor } : {}), ...fontStyle(clinicName) }}>{clinicName}</h3>
            )}
            {description && (
              <p style={{ fontSize: "0.875rem", opacity: 0.8, marginBottom: "16px", marginTop: 0, ...(descriptionColor ? { color: descriptionColor } : {}), ...fontStyle(description || "") }}>{description}</p>
            )}
            {legalLinks.filter((l) => l.label && l.href).length > 0 && (
              <p style={{ fontSize: "0.875rem", opacity: 0.8, marginBottom: "16px", marginTop: 0, display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
                {legalLinks.filter((l) => l.label && l.href).map((link, i, arr) => (
                  <React.Fragment key={i}>
                    <a href={link.href} style={{ color: "inherit", textDecoration: "none", ...fontStyle(link.label) }}>{link.label}</a>
                    {i < arr.length - 1 && <span style={{ opacity: 0.4 }}>|</span>}
                  </React.Fragment>
                ))}
              </p>
            )}
            {address && (
              <p style={{ fontSize: "0.875rem", opacity: 0.8, marginBottom: "8px", marginTop: 0, ...(addressColor ? { color: addressColor } : {}), ...fontStyle(address) }}>{showContactIcons ? "📍 " : ""}{address}</p>
            )}
            {phone && (
              <p style={{ fontSize: "0.875rem", opacity: 0.8, marginBottom: "8px", marginTop: 0, ...(phoneColor ? { color: phoneColor } : {}), ...fontStyle(phone) }}>
                {showContactIcons ? "📞 " : ""}
                <a href={`tel:${phone.replace(/\s/g, "")}`} style={{ color: "inherit", textDecoration: "none" }}>
                  {phone}
                </a>
              </p>
            )}
            {email && (
              <p style={{ fontSize: "0.875rem", opacity: 0.8, marginTop: 0, ...(emailColor ? { color: emailColor } : {}), ...fontStyle(email) }}>
                {showContactIcons ? "✉️ " : ""}
                <a href={`mailto:${email}`} style={{ color: "inherit", textDecoration: "none" }}>
                  {email}
                </a>
              </p>
            )}
            {socialLinks && (
              <div style={{ display: "flex", gap: "16px", marginTop: "16px", alignItems: "center" }}>
                {socialLinks.facebook && (
                  <a
                    href={socialLinks.facebook}
                    style={{ textDecoration: "none", display: "flex", alignItems: "center" }}
                  >
                    <img
                      src="/static/images/social/facebook.png"
                      alt="Facebook"
                      style={{ width: 24, height: 24, objectFit: "contain" }}
                    />
                  </a>
                )}
                {socialLinks.instagram && (
                  <a
                    href={socialLinks.instagram}
                    style={{ textDecoration: "none", display: "flex", alignItems: "center" }}
                  >
                    <img
                      src="/static/images/social/ig.png"
                      alt="Instagram"
                      style={{ width: 24, height: 24, objectFit: "contain" }}
                    />
                  </a>
                )}
                {socialLinks.line && (
                  <a href={socialLinks.line} style={{ textDecoration: "none", display: "flex", alignItems: "center" }}>
                    <img
                      src="/static/images/social/line.png"
                      alt="LINE"
                      style={{ width: 24, height: 24, objectFit: "contain" }}
                    />
                  </a>
                )}
                {socialLinks.youtube && (
                  <a
                    href={socialLinks.youtube}
                    style={{ textDecoration: "none", display: "flex", alignItems: "center" }}
                  >
                    <img
                      src="/static/images/social/youtube.png"
                      alt="Youtube"
                      style={{ width: 24, height: 24, objectFit: "contain" }}
                    />
                  </a>
                )}
                {socialLinks.tiktok && (
                  <a
                    href={socialLinks.tiktok}
                    style={{ textDecoration: "none", display: "flex", alignItems: "center" }}
                  >
                    <img
                      src="/static/images/social/tiktok.png"
                      alt="TikTok"
                      style={{ width: 24, height: 24, objectFit: "contain" }}
                    />
                  </a>
                )}
                {socialLinks.x && (
                  <a href={socialLinks.x} style={{ textDecoration: "none", display: "flex", alignItems: "center" }}>
                    <img
                      src="/static/images/social/twitter.png"
                      alt="X"
                      style={{ width: 24, height: 24, objectFit: "contain" }}
                    />
                  </a>
                )}
                {socialLinks.linkedin && (
                  <a
                    href={socialLinks.linkedin}
                    style={{ textDecoration: "none", display: "flex", alignItems: "center" }}
                  >
                    <img
                      src="/static/images/social/linked.png"
                      alt="LinkedIn"
                      style={{ width: 24, height: 24, objectFit: "contain" }}
                    />
                  </a>
                )}
              </div>
            )}
          </div>
          {safeColumns.map((column, index) => {
            const validLinks = (Array.isArray(column.links) ? column.links : []).filter((link) => link.label && link.href);
            if (validLinks.length === 0) return null;
            return (
            <div key={index} style={alignRight ? { display: "flex", flexDirection: "column", alignItems: "flex-end", textAlign: "right" } : undefined}>
              {column.title && (
                <h4 style={{ fontWeight: 600, marginBottom: "16px", fontSize: "1rem", marginTop: 0, ...fontStyle(column.title) }}>{column.title}</h4>
              )}
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {validLinks.map((link, linkIndex) => (
                  <li key={linkIndex} style={alignRight ? { marginBottom: "8px", textAlign: "right" } : { marginBottom: "8px" }}>
                    <a
                      href={link.href}
                      style={{ fontSize: "0.875rem", opacity: 0.8, color: "inherit", textDecoration: "none", display: alignRight ? "inline-flex" : "flex", alignItems: "center", gap: "8px", flexDirection: alignRight ? "row-reverse" : "row" }}
                    >
                      {link.href.includes("facebook") && <img src="/static/images/social/facebook.png" alt="Facebook" style={{ width: "16px", height: "16px" }} />}
                      {(link.href.includes("line.me") || link.href.includes("lin.ee")) && <img src="/static/images/social/line.png" alt="LINE" style={{ width: "16px", height: "16px" }} />}
                      <span style={fontStyle(link.label)}>{link.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            );
          })}
        </div>
        <div
          style={{
            borderTop: "1px solid rgba(156, 163, 175, 0.4)",
            paddingTop: "32px",
            textAlign: "center",
            fontSize: "0.875rem",
            opacity: 0.7,
          }}
        >
          <span style={{ ...(copyrightColor ? { color: copyrightColor } : {}), ...fontStyle(copyright || `© ${currentYear} ${clinicName}. All rights reserved.`) }}>{copyright || `© ${currentYear} ${clinicName}. All rights reserved.`}</span>
        </div>
      </div>
    </footer>
  );
};
